import { normalizeActivityAssignees } from "../work_order/work_order_calendar/calendarSchedulerUtils";
import { readinessFromPropertyStatus } from "../work_order/work_order_calendar/calendarPresentationUtils";

export function ymdLocal(input = new Date()) {
  const date = input instanceof Date ? input : new Date(input);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function addDaysISO(dateString, amount) {
  const date = new Date(`${dateString}T00:00:00`);
  date.setDate(date.getDate() + amount);
  return ymdLocal(date);
}

export function normalizeWorkOrderBatch(data) {
  if (Array.isArray(data)) return data;
  let rows = data?.paginated || data?.workorders || data?.data || [];
  if (typeof rows === "string") {
    try {
      rows = JSON.parse(rows);
    } catch (_) {
      rows = [];
    }
  }
  return Array.isArray(rows) ? rows : [];
}

export function normalizeTaskType(value) {
  switch (String(value || "").toLowerCase()) {
    case "clean":
    case "cleaning":
    case "housekeeping":
      return "cleaning";
    case "inspection":
    case "inspections":
      return "inspection";
    case "maintenance":
    case "pool_service":
      return "maintenance";
    default:
      return "other";
  }
}

export function isCompletedStatus(status) {
  return ["completed", "complete", "closed", "canceled", "cancelled"].includes(
    String(status || "").toLowerCase(),
  );
}

function isStartedStatus(status) {
  return ["started", "in_progress"].includes(
    String(status || "").toLowerCase(),
  );
}

function inWindow(date, startDate, endDate) {
  const value = String(date || "").slice(0, 10);
  return !!value && value >= startDate && value <= endDate;
}

function overlapsWindow(start, end, windowStart, windowEnd) {
  const from = String(start || "").slice(0, 10);
  const to = String(end || from).slice(0, 10);
  return !!from && from <= windowEnd && to >= windowStart;
}

function teamAssignees(row) {
  return normalizeActivityAssignees({
    assignees:
      row?.kind === "concrete"
        ? row.work_order_assignees
        : row?.assignees || row?.work_order_assignees,
  }).filter(
    (assignee) =>
      assignee.type === "recipient" && assignee.assignment_role !== "owner",
  );
}

function normalizeTask(row, isUnscheduled = false) {
  const assignees = teamAssignees(row);
  return {
    id: row.work_order_id || row.id || row.occurrence_key,
    homeId: String(row.rental_id || ""),
    label: row.summary || row.task_type || "Task",
    date: isUnscheduled ? null : String(row.scheduled_date || "").slice(0, 10),
    status: row.kind === "virtual" ? "new" : row.status || "new",
    priority: String(row.priority || "").toLowerCase(),
    type: normalizeTaskType(row.task_type),
    assignees,
    unassigned: assignees.length === 0,
    recurring: !!(row.is_recurring || row.task_recurrence_rule_id),
    unscheduled: isUnscheduled,
  };
}

function propertyName(rental, fallbackId) {
  return (
    rental?.name ||
    rental?.pms_id ||
    rental?.address ||
    `Rental ${fallbackId}`
  );
}

function readinessForProperty({
  homeId,
  tasks,
  overdueTasks,
  reservations,
  propertyStatus,
  startDate,
  endDate,
}) {
  const explicitReadiness = readinessFromPropertyStatus(propertyStatus);
  const propertyTasks = tasks.filter((task) => task.homeId === homeId);
  const propertyOverdue = overdueTasks.filter((task) => task.homeId === homeId);
  const propertyReservations = reservations.filter(
    (reservation) => String(reservation.rental_id || "") === homeId,
  );
  const blocks = propertyReservations.filter(
    (reservation) =>
      ["BLK", ""].includes(reservation.category || "") &&
      overlapsWindow(
        reservation.check_in,
        reservation.check_out,
        startDate,
        endDate,
      ),
  );
  const arrivals = propertyReservations.filter(
    (reservation) =>
      (reservation.category || "STA") === "STA" &&
      inWindow(reservation.check_in, startDate, endDate),
  );
  const departures = propertyReservations.filter(
    (reservation) =>
      (reservation.category || "STA") === "STA" &&
      inWindow(reservation.check_out, startDate, endDate),
  );
  const missingTurnover = arrivals.some((arrival) => {
    const arrivalDate = String(arrival.check_in || "").slice(0, 10);
    return !propertyTasks.some(
      (task) =>
        task.date === arrivalDate &&
        ["cleaning", "inspection"].includes(task.type),
    );
  });
  const arrivalWork = propertyTasks.filter(
    (task) =>
      ["cleaning", "inspection"].includes(task.type) &&
      arrivals.some(
        (arrival) => String(arrival.check_in || "").slice(0, 10) === task.date,
      ),
  );

  let state = explicitReadiness?.state || "on-track";
  let label = explicitReadiness?.label || "On track";
  if (!explicitReadiness && blocks.length) {
    state = "blocked";
    label = "Blocked";
  } else if (!explicitReadiness && (
    missingTurnover ||
    propertyOverdue.length ||
    propertyTasks.some(
      (task) => task.unassigned && !isCompletedStatus(task.status),
    )
  )) {
    state = "risk";
    label = missingTurnover ? "At risk · no turnover task" : "At risk";
  } else if (!explicitReadiness && (
    arrivals.length &&
    arrivalWork.length &&
    arrivalWork.every((task) => isCompletedStatus(task.status))
  )) {
    state = "ready";
    label = "Ready";
  }

  return {
    state,
    label,
    tasks: propertyTasks.length,
    completed: propertyTasks.filter((task) => isCompletedStatus(task.status)).length,
    arrivals: arrivals.length,
    departures: departures.length,
    blocks: blocks.length,
    overdue: propertyOverdue.length,
  };
}

export function buildOperationsDashboardModel({
  occurrences = [],
  workOrders = [],
  reservations = [],
  rentals = [],
  teamMembers = [],
  propertyStatuses = [],
  startDate,
  endDate,
  today = ymdLocal(),
}) {
  const scheduledTasks = occurrences
    .filter((occurrence) => occurrence?.scheduled_date)
    .map((occurrence) => normalizeTask(occurrence));
  const unscheduledTasks = normalizeWorkOrderBatch(workOrders)
    .filter((workOrder) => !workOrder.scheduled_date && workOrder.task_type)
    .map((workOrder) => normalizeTask(workOrder, true));
  const overdueTasks = normalizeWorkOrderBatch(workOrders)
    .filter(
      (workOrder) =>
        workOrder.scheduled_date &&
        String(workOrder.scheduled_date).slice(0, 10) < today &&
        !isCompletedStatus(workOrder.status),
    )
    .map((workOrder) => normalizeTask(workOrder));
  const openScheduledTasks = scheduledTasks.filter(
    (task) => !isCompletedStatus(task.status),
  );
  const openUnscheduledTasks = unscheduledTasks.filter(
    (task) => !isCompletedStatus(task.status),
  );
  const activeTasks = [...scheduledTasks, ...openUnscheduledTasks];
  const stays = reservations.filter(
    (reservation) => (reservation.category || "STA") === "STA",
  );
  const blocks = reservations.filter(
    (reservation) =>
      ["BLK", ""].includes(reservation.category || "") &&
      overlapsWindow(
        reservation.check_in,
        reservation.check_out,
        startDate,
        endDate,
      ),
  );
  const completed = scheduledTasks.filter((task) =>
    isCompletedStatus(task.status),
  ).length;

  const categories = ["cleaning", "inspection", "maintenance", "other"].map(
    (type) => {
      const rows = scheduledTasks.filter((task) => task.type === type);
      const done = rows.filter((task) => isCompletedStatus(task.status)).length;
      return {
        type,
        total: rows.length,
        completed: done,
        inProgress: rows.filter((task) => isStartedStatus(task.status)).length,
        unassigned: rows.filter(
          (task) => task.unassigned && !isCompletedStatus(task.status),
        ).length,
        percent: rows.length ? Math.round((done / rows.length) * 100) : 0,
      };
    },
  );

  const rentalById = new Map(
    rentals.map((rental) => [String(rental.rental_id), rental]),
  );
  const propertyStatusByRentalId = new Map(
    propertyStatuses.map((status) => [String(status.rental_id), status]),
  );
  const activeHomeIds = new Set(
    scheduledTasks.map((task) => task.homeId).filter(Boolean),
  );
  reservations.forEach((reservation) => {
    if (
      reservation.rental_id &&
      (inWindow(reservation.check_in, startDate, endDate) ||
        inWindow(reservation.check_out, startDate, endDate) ||
        overlapsWindow(
          reservation.check_in,
          reservation.check_out,
          startDate,
          endDate,
        ))
    ) {
      activeHomeIds.add(String(reservation.rental_id));
    }
  });
  propertyStatuses.forEach((status) => {
    if (status.rental_id) activeHomeIds.add(String(status.rental_id));
  });

  const stateOrder = { blocked: 0, risk: 1, "on-track": 2, ready: 3 };
  const properties = [...activeHomeIds]
    .map((homeId) => {
      const rental = rentalById.get(homeId);
      return {
        id: homeId,
        name: propertyName(rental, homeId),
        address: rental?.address || "",
        ...readinessForProperty({
          homeId,
          tasks: scheduledTasks,
          overdueTasks,
          reservations,
          propertyStatus: propertyStatusByRentalId.get(homeId) || null,
          startDate,
          endDate,
        }),
      };
    })
    .sort(
      (a, b) =>
        stateOrder[a.state] - stateOrder[b.state] || a.name.localeCompare(b.name),
    );

  const team = teamMembers
    .filter((member) => !member.deleted)
    .map((member) => {
      const id = String(member.recipient_id);
      const assigned = activeTasks.filter((task) =>
        task.assignees.some((assignee) => String(assignee.id) === id),
      );
      const done = assigned.filter((task) => isCompletedStatus(task.status)).length;
      return {
        id,
        name:
          [member.first_name, member.last_name].filter(Boolean).join(" ") ||
          member.name ||
          member.email ||
          `Team member ${id}`,
        email: member.email || "",
        avatar:
          member.profile_image_url || member.avatar_url || member.image_url || "",
        assigned: assigned.length,
        inProgress: assigned.filter((task) => isStartedStatus(task.status)).length,
        completed: done,
        percent: assigned.length ? Math.round((done / assigned.length) * 100) : 0,
      };
    })
    .sort((a, b) => b.assigned - a.assigned || a.name.localeCompare(b.name));

  return {
    stats: {
      unassigned: [...openScheduledTasks, ...openUnscheduledTasks].filter(
        (task) => task.unassigned,
      ).length,
      overdue: overdueTasks.length,
      dueToday: scheduledTasks.filter((task) => task.date === today).length,
      arrivals: stays.filter((stay) => inWindow(stay.check_in, startDate, endDate))
        .length,
      departures: stays.filter((stay) =>
        inWindow(stay.check_out, startDate, endDate),
      ).length,
      blocked: new Set(blocks.map((block) => String(block.rental_id))).size,
      unscheduled: openUnscheduledTasks.length,
      totalTasks: scheduledTasks.length,
      completed,
      completionPercent: scheduledTasks.length
        ? Math.round((completed / scheduledTasks.length) * 100)
        : 0,
    },
    categories,
    properties,
    team,
  };
}
